import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useOrganizationSwitch } from '../../hooks/useOrganizationSwitch';
import { hasAdminRole } from '../../utils/roleUtils';
import { 
  ChevronUp, 
  Check, 
  Plus,
  Settings,
  X,
  Copy
} from 'lucide-react';

interface OrganizationSelectorProps {
  currentOrganizationId?: string;
  onOrganizationChange: (organizationId: string) => void;
  className?: string;
}

const OrganizationSelector: React.FC<OrganizationSelectorProps> = ({
  currentOrganizationId,
  onOrganizationChange,
  className = ''
}) => {
  const { organizations, hasRole } = useAuth();
  const { switchOrganization, isSwitching, switchingOrgId } = useOrganizationSwitch();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const canViewIds = hasAdminRole(hasRole);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentOrg = organizations.find(org => 
    org.organization.id === currentOrganizationId
  );

  const handleOrgSelect = async (organizationId: string) => {
    if (organizationId === currentOrganizationId) {
      setIsOpen(false);
      return;
    }

    try {
      await switchOrganization(organizationId);
      setIsOpen(false);
      
      // Call the parent's onOrganizationChange for any additional handling
      onOrganizationChange(organizationId);
    } catch (_error) {
      // Error handling is done in the hook
      // console.error('Organization switch error:', error);
    }
  };

  const handleCreateNew = () => {
    // Navigate to organization creation page
    // console.log('Navigating to organization setup...');
    navigate('/organization/setup');
  };

  const copyOrgId = async (orgId: string) => {
    try {
      await navigator.clipboard.writeText(orgId);
      setCopiedId(orgId);
      // Hide notification after 2 seconds
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to copy organization ID:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = orgId;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedId(orgId);
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={isSwitching}
        className={`
          flex items-center gap-3 px-4 py-3 rounded-lg border border-gray-200 
          hover:border-gray-300 hover:bg-gray-50 transition-all duration-200
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
          disabled:opacity-50 disabled:cursor-not-allowed
          ${isOpen ? 'border-blue-300 bg-blue-50' : ''}
        `}
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {/* Organization Logo */}
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-blue-600 text-sm font-bold text-white">
            {currentOrg?.organization.name?.split(' ').map((word: string) => word.charAt(0)).join('').substring(0, 4) || 'ORG'}
          </div>
          
          {/* Organization Name */}
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-gray-900 truncate">
              {currentOrg?.organization.name || 'Select Organization'}
            </div>
          </div>
        </div>
        
        {/* Chevron */}
        <ChevronUp className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-xl z-50 w-80">
          {/* Header */}
          <div className="px-5 py-4 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base font-semibold text-gray-900">Organizations</h3>
                <p className="text-sm text-gray-600 mt-1">My Organizations</p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => navigate('/settings')}
                  className="text-sm text-gray-600 hover:text-gray-800 flex items-center gap-1 transition-colors"
                >
                  <span>Manage</span>
                  <Settings className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Organizations List */}
          <div className="py-2">
            {organizations.length === 0 ? (
              <div className="px-5 py-8 text-center">
                <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <span className="text-lg font-bold text-gray-400">O</span>
                </div>
                <p className="text-sm text-gray-500">No organizations available</p>
              </div>
            ) : (
              organizations.map((org) => (
                <button
                  key={org.organization.id}
                  onClick={() => handleOrgSelect(org.organization.id)}
                  disabled={isSwitching && switchingOrgId === org.organization.id}
                  className={`
                    w-full px-5 py-4 text-left hover:bg-gray-50 transition-colors
                    disabled:opacity-50 disabled:cursor-not-allowed
                    ${org.organization.id === currentOrganizationId ? 'bg-blue-50' : ''}
                  `}
                >
                  <div className="flex items-start gap-3">
                    {/* Organization Logo */}
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-600 text-sm font-bold text-white">
                      {org.organization.name?.split(' ').map((word: string) => word.charAt(0)).join('').substring(0, 4) || 'ORG'}
                    </div>
                    
                    {/* Organization Details */}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="text-sm font-semibold text-gray-900 leading-tight">
                          {org.organization.name}
                        </h4>
                        
                        {/* Active Indicator */}
                        {org.organization.id === currentOrganizationId && (
                          <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 ml-2">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        )}
                        
                        {/* Loading Indicator */}
                        {isSwitching && switchingOrgId === org.organization.id && (
                          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 ml-2">
                            <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        {canViewIds && (
                          <>
                            <span>Organization ID: {org.organization.display_id || org.organization.id}</span>
                            <div className="relative">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  copyOrgId(org.organization.display_id || org.organization.id);
                                }}
                                className="hover:text-gray-700 transition-colors p-1 hover:bg-gray-100 rounded"
                                title="Copy Organization ID"
                              >
                                <Copy className="w-3 h-3" />
                              </button>
                              
                              {/* Copy Notification */}
                              {copiedId === (org.organization.display_id || org.organization.id) && (
                                <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-green-600 text-white text-xs px-2 py-1 rounded shadow-lg z-50 whitespace-nowrap">
                                  Copied!
                                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-2 border-r-2 border-t-2 border-transparent border-t-green-600"></div>
                                </div>
                              )}
                            </div>
                            <span>•</span>
                          </>
                        )}
                        <span>Free</span>
                      </div>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>

          {/* Footer */}
          <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 rounded-b-lg">
            <button
              onClick={handleCreateNew}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              Create New Organization
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationSelector;